/**
 * Map entity field type hints to compatible CRM DataType values.
 */
const ENTITY_TO_CRM_TYPES: Record<string, string[]> = {
  string: ['STRING', 'LONG_STRING'],
  long_string: ['LONG_STRING', 'STRING'],
  number: ['NUMBER'],
  boolean: ['BOOLEAN'],
  date: ['DATE', 'DATE_TIME'],
  datetime: ['DATE_TIME', 'DATE'],
  object: ['JSON', 'STRING', 'LONG_STRING'],
}

export function compatibleCrmTypes(entityType?: string): string[] | null {
  if (!entityType) return null
  return ENTITY_TO_CRM_TYPES[entityType.toLowerCase()] ?? null
}

export function isCrmTypeCompatible(
  entityType: string | undefined,
  crmType: string | undefined,
): boolean {
  if (!entityType || !crmType) return true
  const allowed = compatibleCrmTypes(entityType)
  if (!allowed) return true
  return allowed.includes(crmType.toUpperCase())
}

function crmFieldHasOptions(field: {
  hasOptions?: boolean
  options?: unknown[]
}): boolean {
  return Boolean(field.hasOptions || field.options?.length)
}

/** Enum entity fields (with options) may only map to STRING fields that have options. */
export function isEntityFieldCompatibleWithCrmField(
  entityType: string | undefined,
  entityHasOptions: boolean,
  crmField: {
    type?: string
    hasOptions?: boolean
    options?: unknown[]
  },
): boolean {
  if (!isCrmTypeCompatible(entityType, crmField.type)) return false

  const targetHasOptions = crmFieldHasOptions(crmField)

  if (entityHasOptions) {
    const crmType = crmField.type?.toUpperCase()
    if (crmType !== 'STRING' && crmType !== 'LONG_STRING') return false
    // Option-backed CRM fields are ideal; plain string fields still work via
    // source-enum fallback in the value-map UI when definitions are missing.
    return targetHasOptions || crmType === 'STRING' || crmType === 'LONG_STRING'
  }

  // Plain fields must not map to select/enum CRM fields (e.g. source, stage).
  return !targetHasOptions
}

export type SelectFieldItem = {
  value: string
  label: string
  description?: string
}

export function buildFieldSelectItems(params: {
  fields: Array<{
    id: string
    handle: string
    name: string
    type?: string
    isUnique?: boolean
    hasOptions?: boolean
    options?: Array<{ value: string; label: string }>
  }>
  entityType?: string
  /** Source entity field declares enum/options */
  entityHasOptions?: boolean
  /** When set, only include unique CRM fields (plus currently selected) */
  uniqueOnly?: boolean
  /** Always include this field id even if filtered out */
  selectedId?: string | null
  emptyLabel?: string
}): SelectFieldItem[] {
  const {
    fields,
    entityType,
    entityHasOptions = false,
    uniqueOnly = false,
    selectedId,
    emptyLabel = '— Not connected —',
  } = params

  const filtered = fields.filter((f) => {
    if (selectedId && f.id === selectedId) return true
    if (uniqueOnly && !f.isUnique) return false
    if (
      !isEntityFieldCompatibleWithCrmField(entityType, entityHasOptions, f)
    ) {
      return false
    }
    return true
  })

  return [
    { value: '', label: emptyLabel },
    ...filtered.map((f) => ({
      value: f.id,
      label: f.name || f.handle,
      description: f.handle,
    })),
  ]
}
